import { ArrowLeft, Sun, Moon, ShoppingCart, Phone, Bike, Package, Utensils, X, Check } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { useThemeStore } from "../stores/useThemeStore"
import { useCartStore } from "../stores/useCartStore"
import { useAuthStore } from "../stores/useAuthStore"
import { useServicePalette } from "../hooks/useServicePalette"

interface HeaderProps {
  title: string
  showBack?: boolean
  showCart?: boolean
  showThemeToggle?: boolean
  showCallWaiter?: boolean
  showServiceIndicator?: boolean
  tableNumber?: number
  tableStatus?: string
  onCallWaiter?: () => void
  onBack?: () => void
}

export function Header({
  title,
  showBack = false,
  showCart = false,
  showThemeToggle = false,
  showCallWaiter = false,
  showServiceIndicator = false,
  tableNumber,
  tableStatus,
  onCallWaiter,
  onBack,
}: HeaderProps) {
  const navigate = useNavigate()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const { isDark, toggleTheme } = useThemeStore()
  const { getItemCount } = useCartStore()
  const cartItemCount = getItemCount()
  const palette = useServicePalette()
  const { isTakeAway, orderMode, tableId } = useAuthStore()

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const currentTableId = tableId || window.location.pathname.split("/")[2] || 'default';

  const getModeLabel = () => {
    if (!isTakeAway) return `Mesa ${tableNumber || ''}`;
    return orderMode === 'delivery' ? 'Delivery' : 'Retiro';
  };

  return (
    <>
      <header className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl text-gray-900 dark:text-white px-4 py-4 flex items-center justify-between shadow-lg border-b border-gray-200/50 dark:border-gray-800/50 sticky top-0 z-40 h-20">
        {/* Left Section: Back & Title */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {showBack && (
            <button
              onClick={handleBack}
              className="p-3 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-2xl transition-all duration-300 hover:scale-105 backdrop-blur-sm shrink-0"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </button>
          )}

          <div className="min-w-0">
            <h1 className="font-bold text-xl text-gray-900 dark:text-white gradient-text truncate">{title}</h1>
            {tableNumber && !showServiceIndicator && (
              <p className={`text-sm font-semibold ${palette.textAccent} ${palette.bgDark} truncate`}>
                Mesa {tableNumber} • {tableStatus}
              </p>
            )}
          </div>
        </div>

        {/* Center Section: Service Indicator */}
        {showServiceIndicator && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <button
              onClick={() => setShowConfirmModal(true)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border ${palette.borderAccent} bg-opacity-10 backdrop-blur-md transition-all duration-300 hover:scale-105 group whitespace-nowrap shadow-sm`}
              style={{ backgroundColor: 'rgba(var(--primary-color), 0.1)' }}
            >
              <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${palette.primaryFrom} ${palette.primaryTo} flex items-center justify-center text-white shadow-sm group-hover:rotate-12 transition-transform`}>
                {isTakeAway ? (
                  orderMode === 'delivery' ? <Bike className="w-3.5 h-3.5" /> : <Package className="w-3.5 h-3.5" />
                ) : (
                  <Utensils className="w-3.5 h-3.5" />
                )}
              </div>
              <span className={`text-[10px] sm:text-[11px] font-black uppercase tracking-wider ${palette.textAccent}`}>
                {getModeLabel()}
              </span>
            </button>
          </div>
        )}

        {/* Right Section: Actions */}
        <div className="flex items-center gap-2 justify-end shrink-0">
          {showCallWaiter && !isTakeAway && (
            <button
              onClick={onCallWaiter}
              className={`bg-gradient-to-r ${palette.primaryFrom} ${palette.primaryTo} hover:${palette.primaryFromHover} hover:${palette.primaryToHover} px-4 py-2 rounded-2xl text-sm font-semibold transition-all duration-300 flex items-center gap-2 text-white shadow-lg hover:shadow-xl hover:scale-105 hidden sm:flex`}
            >
              <Phone className="w-4 h-4" />
              Mozo
            </button>
          )}

          {showCart && (
            <button
              onClick={() => navigate(`/cart/${currentTableId}`)}
              className="relative p-3 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-2xl transition-all duration-300 hover:scale-105 backdrop-blur-sm"
            >
              <ShoppingCart className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              {cartItemCount > 0 && (
                <span className={`absolute -top-1 -right-1 bg-gradient-to-r ${palette.badgeFrom} ${palette.badgeTo} text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-lg animate-pulse`}>
                  {cartItemCount}
                </span>
              )}
            </button>
          )}

          {showThemeToggle && (
            <button
              onClick={toggleTheme}
              className="p-3 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-2xl transition-all duration-300 hover:scale-105 backdrop-blur-sm"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              ) : (
                <Moon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              )}
            </button>
          )}
        </div>
      </header>

      {/* Modal de Confirmación de Cambio de Modo */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowConfirmModal(false)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl w-full max-w-sm border border-gray-100 dark:border-white/5 animate-in zoom-in duration-300">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className={`w-20 h-20 rounded-[2rem] bg-gradient-to-br ${palette.primaryFrom} ${palette.primaryTo} flex items-center justify-center text-white shadow-xl shadow-primary/20`}>
                {isTakeAway ? (
                  orderMode === 'delivery' ? <Bike className="w-10 h-10" /> : <Package className="w-10 h-10" />
                ) : (
                  <Utensils className="w-10 h-10" />
                )}
              </div>

              <div className="space-y-2">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                  Has seleccionado el modo {getModeLabel()}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  ¿Quieres cambiar tu tipo de pedido?
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 w-full pt-4">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex items-center justify-center gap-2 py-4 px-4 rounded-2xl bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 font-bold text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-all active:scale-95 text-center"
                >
                  No, mantener
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    navigate(`/order-timing/${currentTableId}`);
                  }}
                  className={`flex items-center justify-center gap-2 py-4 px-4 rounded-2xl bg-gradient-to-r ${palette.primaryFrom} ${palette.primaryTo} text-white font-bold text-sm shadow-lg shadow-primary/20 hover:scale-[1.02] transition-all active:scale-95 text-center`}
                >
                  Sí, cambiar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
